export function extractTrackingList(payload) {
  if (!payload) return [];
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.data?.trackings)) return payload.data.trackings;
  if (Array.isArray(payload?.trackings)) return payload.trackings;
  if (Array.isArray(payload?.data?.account_behaviour_trackings)) {
    return payload.data.account_behaviour_trackings;
  }
  if (Array.isArray(payload?.account_behaviour_trackings)) {
    return payload.account_behaviour_trackings;
  }
  if (Array.isArray(payload?.data?.data)) return payload.data.data;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload?.records)) return payload.records;
  return [];
}

/** Numeric account PK from a tracking row. Never use employee_id (that is emp code). */
export function trackingAccountIdFromRow(row) {
  if (!row || typeof row !== 'object') return null;
  const raw = row.account_id ?? row.accountId ?? row.account?.id ?? null;
  if (raw == null || raw === '') return null;
  const n = Number(raw);
  return Number.isFinite(n) && n > 0 ? n : null;
}

export function getNonComplianceCount(item) {
  if (!item || typeof item !== 'object') return null;
  const value = item.non_compliance_count ?? item.nonComplianceCount ?? item.occurrences;
  if (value == null || value === '') return null;
  const num = Number(value);
  return Number.isFinite(num) ? num : null;
}

export function formatComplianceStatus(item) {
  if (!item || typeof item !== 'object') return '—';
  const status = String(item.compliance_status ?? item.complianceStatus ?? '')
    .trim()
    .toLowerCase();
  if (status === 'compliance' || status === 'compliant') return 'Compliance';
  if (
    status === 'non_compliance' ||
    status === 'non-compliance' ||
    status === 'noncompliance'
  ) {
    return 'Non Compliance';
  }
  if (item.rating != null && item.rating !== '') return String(item.rating);
  const remarks = String(item.manager_remarks ?? item.remarks ?? '').trim();
  if (/^compliance$/i.test(remarks)) return 'Compliance';
  if (/^non\s*compliance$/i.test(remarks)) return 'Non Compliance';
  return '—';
}

function mapPerformanceBehaviours(list) {
  return (Array.isArray(list) ? list : []).map((item) => {
    const count = getNonComplianceCount(item);
    const rating = Number(item?.rating);
    const percentageFromRating = Number.isFinite(rating) ? (rating / 5) * 100 : null;
    const percentage =
      item?.percentage != null && item?.percentage !== ''
        ? item.percentage
        : percentageFromRating;
    return {
      behaviour_id: item?.behaviour_id,
      behaviour_name: item?.behaviour_name,
      non_compliance_count: count == null ? 0 : count,
      percentage,
    };
  });
}

function overallFromBehaviours(behaviours) {
  if (!behaviours.length) return null;
  const compliant = behaviours.filter((b) => Number(b.non_compliance_count) === 0).length;
  return (compliant / behaviours.length) * 100;
}

export function extractPerformance(payload) {
  const raw = payload?.data?.performance ?? payload?.performance ?? null;
  if (!raw || typeof raw !== 'object') {
    return { overallPercentage: null, behaviours: [], totalNonCompliance: 0 };
  }
  const behaviours = mapPerformanceBehaviours(raw.behaviours);
  const totalNonCompliance = behaviours.reduce(
    (sum, item) => sum + Number(item.non_compliance_count || 0),
    0
  );
  return {
    overallPercentage:
      raw.overall_percentage ?? raw.overallPercentage ?? overallFromBehaviours(behaviours),
    behaviours,
    totalNonCompliance,
  };
}

export function performanceFromTrackingRow(row) {
  if (!row) return { overallPercentage: null, behaviours: [], totalNonCompliance: 0 };
  const source = Array.isArray(row.behaviours) ? row.behaviours : [];
  const behaviours = mapPerformanceBehaviours(source);
  const totalNonCompliance = behaviours.reduce(
    (sum, item) => sum + Number(item.non_compliance_count || 0),
    0
  );
  return {
    overallPercentage:
      row.monthlyPerformance ??
      row.yearlyPerformance ??
      row.monthly_performance ??
      row.yearly_performance ??
      overallFromBehaviours(behaviours),
    behaviours,
    totalNonCompliance,
  };
}

export function formatPercent(value) {
  if (value == null || value === '') return '—';
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) return '—';
    return trimmed.endsWith('%') ? trimmed : `${trimmed}%`;
  }
  const num = Number(value);
  if (Number.isNaN(num)) return String(value);
  return `${Number.isInteger(num) ? num : num.toFixed(2)}%`;
}

export function computeTrackingComplianceSummary(behaviours) {
  const list = Array.isArray(behaviours) ? behaviours : [];
  if (!list.length) {
    return { complianceRate: null, compliant: 0, nonCompliant: 0, total: 0 };
  }
  let compliant = 0;
  let nonCompliant = 0;
  list.forEach((item) => {
    const status = formatComplianceStatus(item);
    if (status === 'Non Compliance' || Number(getNonComplianceCount(item)) > 0) {
      nonCompliant += 1;
    } else {
      compliant += 1;
    }
  });
  const total = list.length;
  return {
    complianceRate: total > 0 ? (compliant / total) * 100 : null,
    compliant,
    nonCompliant,
    total,
  };
}
